import * as backend from './backend'
import { transformToNameKeyedMap, sanitizeFilters } from 'utils'

/**
 *
 * @typedef Property
 * @type {object}
 * @property {string} id
 * @property {string} address
 * @property {string} description
 * @property {string} city
 * @property {string} state
 * @property {number} zip
 * @property {number} price
 * @property {number} lat
 * @property {number} lng
 * @property {number} sqft
 * @property {number} price
 * @property {number} beds
 * @property {number} baths
 * @property {number} year
 * @property {boolean} showZestimate
 * @property {string} amenities
 * @property {string} homeType
 * @property {number} arv
 * @property {number} repairEstimate
 * @property {string} type
 * @property {string[]} images
 * @property {string} defaultImage
 * @property {number} createdAt
 * @property {number} updatedAt
 *
 * @typedef ImageType
 * @type {object}
 * @property {string} name
 * @property {boolean} default
 * @property {string} url
 *
 * @typedef GeoPoint
 * @type {object}
 * @property {number} lat
 * @property {number} lng
 *
 * @typedef GeoRect
 * @type {object}
 * @property {GeoPoint} northeast
 * @property {GeoPoint} southwest
 *
 * @typedef PropertyFilter
 * @type {object}
 * @property {string} name
 * @property {string} type
 * @property {string[]} value
 *
 * @typedef Filter
 * @type {object}
 * @property {number} areaMin
 * @property {number} areaMax
 * @property {number} baths
 * @property {string} beds
 * @property {string} type
 * @property {string[]} home_types
 * @property {number} priceMin
 * @property {number} princeMax
 * @property {string[]} types
 *
 * @typedef PropertiesResponse
 * @type {object}
 * @property {Map[[id,Property]]} properties ( A native map string-id keyed with Property as values )
 * @property {number} propertyCount
 *
 * @param {GeoRect} geoRect
 * @param {Filter} filters
 * @param {int} limit
 * @param {int} offset
 * @return {Promise<PropertiesResponse>}
 *
 * @typedef PropertyResponse
 * @type {object}
 * @property {} property
 *
 * @typedef AuthRequest
 * @type {object}
 * @property {string} facebook_token
 * @property {string} google_token
 * @property {string} email
 * @property {string} password
 *
 * @typedef AuthResponse
 * @type {object}
 * @property {string} token
 * @property {object} user
 *
 * @typedef RegisterRequest
 * @type {object}
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} email
 * @property {string} phone
 * @property {string} location
 * @property {string} zip
 * @property {string} password
 *
 */

// takes array of properties and return a keyIdEyedMap, adding the lng attribute
const fitProperties = properties =>
  new Map(
    properties.map(el => [
      el.id,
      {
        ...el,
        lng: el.lng || el.lon,
      },
    ])
  )

const fromGeoRect = geoRect => ({
  s: geoRect.southwest.lat,
  n: geoRect.northeast.lat,
  w: geoRect.southwest.lng,
  e: geoRect.northeast.lng,
})

const requireAuthToken = token => {
  if (!token) throw Error(`Auth token not found`)
}

/**
 * @return {Promise<[PropertiesResponse]>}
 */
export async function fetchProperties(geoRect, filters, limit, offset) {
  const { properties, propertyCount } = await backend.fetchPropertiesInGeoRect(fromGeoRect(geoRect), sanitizeFilters(filters), limit, offset)
  return {
    properties: fitProperties(properties),
    propertyCount,
  }
}

/**
 * @return {Promise<[PropertiesResponse]>}
 */
export async function fetchSavedProperties(token) {
  requireAuthToken(token)
  const { savedProperties } = await backend.savedProperties(token)
  return {
    properties: fitProperties(savedProperties.filter(obj => obj)),
  }
}

/**
 * @return {Promise<[PropertyResponse]>}
 */
export async function fetchProperty(id, token) {
  const { property, errors } = await backend.fetchProperty(id, token)
  return {
    property,
    errors,
  }
}

/**
 * @typedef PropertyFilterResponse
 * @type {object}
 * @property {Map[[string,PropertyFilter]]} properties ( A native map name keyed with PropertyFilter as values )
 *
 * @return {Promise<[PropertyFilterResponse]>}
 */
export async function fetchPropertyFilters() {
  const propertyFilters = await backend.fetchPropertyFilters()

  return transformToNameKeyedMap(propertyFilters)
}

/**
 *
 * @return {Promise<PropertiesResponse>}
 */
export async function fetchRecentProperties({ lat, lng }, limit) {
  const { properties, propertyCount } = await backend.fetchRecentProperties({ lat, lon: lng }, limit)
  return {
    properties: fitProperties(properties),
    propertyCount,
  }
}

/**
 *
 * @return {Promise<{Map[[id,Property]]}>}
 */
export async function fetchSimilarProperties(id, limit) {
  const properties = await backend.fetchSimilarProperties(id, limit)
  return fitProperties(properties)
}

/**
 * @param {AuthRequest}
 * @return {Promise<AuthResponse>}
 *
 */
export async function authenticate(authRequest) {
  const { user, token, errors } = await backend.authenticate(authRequest)
  return {
    user,
    token,
    errors,
  }
}

/**
 * @param {RegisterRequest}
 * @return {Promise<AuthResponse>}
 *
 */
export async function createUser(reqUser, authRequest) {
  const { user, errors, token } = await backend.createUser({ ...reqUser, ...authRequest })
  return {
    user,
    token,
    errors,
  }
}

export async function resendActivationEmail(email) {
  const { errors } = await backend.resendActivationEmail(email)
  return {
    errors,
  }
}

export async function resetPassword(email) {
  const { errors } = await backend.resetPassword(email)
  return {
    errors,
  }
}

export async function updatePassword({ password, resetPasswordToken }) {
  const { errors } = await backend.updatePassword({ password, resetPasswordToken })
  return {
    errors,
  }
}

export async function userInfo(token) {
  requireAuthToken(token)
  const { user } = await backend.userInfo(token)
  return {
    user,
  }
}
export async function addPropertyToFavorites(propertyId, token) {
  requireAuthToken(token)

  return await backend.addPropertyToFavorites(propertyId, token)
}

export async function removePropertyFromFavorites(propertyId, token) {
  requireAuthToken(token)

  return await backend.removePropertyFromFavorites(propertyId, token)
}

export async function saveSearch(token, geoRect, filters = {}, location) {
  requireAuthToken(token)

  return await backend.saveSearch(token, fromGeoRect(geoRect), sanitizeFilters(filters), location)
}

export async function removeSearch(token, id) {
  requireAuthToken(token)

  return await backend.removeSearch(token, id)
}

export async function fetchSavedSearches(token, limit) {
  requireAuthToken(token)

  return await backend.fetchSavedSearches(token, limit)
}

export async function removeNotification(token, id) {
  requireAuthToken(token)

  return await backend.removeNotification(token, id)
}

export async function fetchNotifications(token, limit) {
  requireAuthToken(token)

  return await backend.fetchNotifications(token, limit)
}

export async function markNotificationRead(token) {
  requireAuthToken(token)

  return await backend.markNotificationRead(token)
}

export async function updateUser(params, token) {
  requireAuthToken(token)
  const { errors } = await backend.updateUser(params, token)
  return {
    errors,
  }
}

export async function contactUs(params) {
  const { errors } = await backend.contactUs(params)
  return {
    errors,
  }
}

export async function updateUserAvatar(avatar, token) {
  requireAuthToken(token)
  const { errors, avatarUrl } = await backend.updateUserAvatar(avatar, token)
  return {
    avatarUrl,
    errors,
  }
}

export async function createSubscription(params, token) {
  requireAuthToken(token)
  const { errors } = await backend.createSubscription(params, token)
  return {
    errors,
  }
}

export async function cancelSubscription(token) {
  requireAuthToken(token)
  const { errors } = await backend.cancelSubscription(token)
  return {
    errors,
  }
}

export async function flagProperty(data) {
  requireAuthToken(data.token)
  const { errors } = await backend.flagProperty(data)
  return {
    errors,
  }
}

/**
 *
 * @todo implement: integrate with backend
 *
 * @typedef Franchisee
 * @type {object}
 * @property {string} id
 * @property {string} address
 * @property {string} description
 * @property {string} city
 * @property {string} state
 * @property {string} zip
 * @property {number} lat
 * @property {number} lng
 * @property {string} profile_image_url
 * @property {string} bannerImageUrl
 * @property {number} price
 * @property {string} country
 * @property {Property[]} properties
 * @property {string} subdomain
 * @property {string} ad_council
 *
 * @return {Promise<Franchisee>}
 */
export async function fetchFranchisee(id) {
  throw Error('not implemented')
}

/**
* @typedef AdCouncil
* @type {object}
* @property {string} id
* @property {string} description
* @property {string} profileImageUrl
* @property {string} bannerImageUrl
* @property {string} location
* @property {Property[]} properties
* @property {string} subdomain
* @property {number} totalFranchisees
* @property {string} phone
* @property {Franchisee[]} franchisees
/

/**
 * @todo implement: integrate with backend
 * @return {Promise<AdCouncil>}
 */
export async function fetchAdCouncil(id) {
  throw Error('not implemented')
}



// WEBPACK FOOTER //
// ./src/services/api/index.js